import { useState } from "react";
import Add from "@mui/icons-material/Add";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Save from "@mui/icons-material/Save";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { Section12Specialism } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import {
  Banner,
  BannerList,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export function Section12SpecialismsManagementPage() {
  const [{ data: specialisms, loading }, refetchSpecialisms] =
    apiHooks.section12.getAllSpecialisms();

  const [newSpecialismName, setNewSpecialismName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleCreate = async () => {
    if (!newSpecialismName.trim()) {
      return;
    }
    try {
      await api.section12.createSpecialism({
        specialismName: newSpecialismName.trim(),
      });
      setNewSpecialismName("");
      renderSuccessToast({ message: "Specialism created successfully" });
      void refetchSpecialisms();
    } catch (error) {
      console.error("Failed to create specialism:", error);
      renderErrorToast({ message: "Failed to create specialism" });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editingName.trim()) {
      return;
    }
    try {
      await api.section12.updateSpecialism(id, {
        specialismName: editingName.trim(),
      });
      setEditingId(null);
      setEditingName("");
      renderSuccessToast({ message: "Specialism updated successfully" });
      void refetchSpecialisms();
    } catch (error) {
      console.error("Failed to update specialism:", error);
      renderErrorToast({ message: "Failed to update specialism" });
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this specialism? This may affect users who have selected it.",
      )
    ) {
      return;
    }
    try {
      await api.section12.deleteSpecialism(id);
      renderSuccessToast({ message: "Specialism deleted successfully" });
      void refetchSpecialisms();
    } catch (error) {
      console.error("Failed to delete specialism:", error);
      renderErrorToast({ message: "Failed to delete specialism" });
    }
  };

  const startEditing = (specialism: Section12Specialism) => {
    setEditingId(specialism.id);
    setEditingName(specialism.specialismName);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <LoadingSpinner label="Loading specialisms..." />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Section 12 Specialisms
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Manage the list of specialisms available for Section 12 doctors to
        select.
      </Typography>
      {/* Add new specialism */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginBottom: "1em",
          gap: 2,
        }}
      >
        <TextField
          sx={{ flexGrow: 1 }}
          value={newSpecialismName}
          onChange={(e) => {
            setNewSpecialismName(e.target.value);
          }}
          placeholder="Enter new specialism name"
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void handleCreate();
            }
          }}
        />
        <IconButton
          color="primary"
          disabled={!newSpecialismName.trim()}
          onClick={() => {
            void handleCreate();
          }}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "grey.300" },
          }}
        >
          <Add />
        </IconButton>
      </Box>
      <Box sx={{ marginBottom: "1em" }}>
        <Banner
          title="Deleting a specialism will not remove it from users who have already selected it, but it will no longer be available for new selections."
          bannerType={BannerList.INFO}
          dense={true}
        />
      </Box>
      {/* Specialisms table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="specialisms table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Specialism Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 150 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!specialisms || specialisms.length === 0) && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No specialisms defined yet. Add one above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {specialisms?.map((specialism) => (
              <TableRow
                key={specialism.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {editingId === specialism.id ? (
                    <TextField
                      value={editingName}
                      onChange={(e) => {
                        setEditingName(e.target.value);
                      }}
                      size="small"
                      fullWidth
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          void handleUpdate(specialism.id);
                        } else if (e.key === "Escape") {
                          cancelEditing();
                        }
                      }}
                    />
                  ) : (
                    specialism.specialismName
                  )}
                </TableCell>
                <TableCell>
                  {editingId === specialism.id ? (
                    <>
                      <IconButton
                        onClick={() => {
                          void handleUpdate(specialism.id);
                        }}
                        color="primary"
                        title="Save"
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        onClick={cancelEditing}
                        color="default"
                        title="Cancel"
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        onClick={() => {
                          startEditing(specialism);
                        }}
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          void handleDelete(specialism.id);
                        }}
                        color="error"
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
